import type { FailedWinnerRow, ImportWinnerRow } from '@api/editions-api';
import { escapeCsvField, parseCsvHeaders, splitCsvLine } from '@utils/csv-utils';

const REQUIRED_HEADERS = ['category_external_id', 'position'] as const;

/**
 * The winner key. Which one a file must carry depends on the edition's site: an
 * SSO edition identifies winners by Docplanner doctor id, a PMS one by HubSpot
 * id. One of the two columns must be present; the server decides which is right
 * for the edition and reports a mismatch per row.
 */
const KEY_HEADERS = ['dp_doctor_id', 'hsid'] as const;

export interface WinnerPreview {
  categoryExternalId: number;
  winnerCount: number;
}

export function parseWinnerRows(content: string): ImportWinnerRow[] {
  const lines = content.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];

  const { headers, delimiter } = parseCsvHeaders(lines[0]!);
  const headerIndex = new Map(headers.map((h, i) => [h, i]));

  const getValue = (fields: string[], header: string): string =>
    fields[headerIndex.get(header) ?? -1]?.trim() ?? '';

  return lines.slice(1).reduce<ImportWinnerRow[]>((acc, line) => {
    const fields = splitCsvLine(line, delimiter);

    const categoryExternalId = Number(getValue(fields, 'category_external_id'));
    const dpDoctorIdRaw = getValue(fields, 'dp_doctor_id');
    const hsid = getValue(fields, 'hsid');
    const position = Number(getValue(fields, 'position'));
    const participantName = getValue(fields, 'participant_name');

    if (!Number.isFinite(categoryExternalId) || categoryExternalId <= 0) return acc;
    if (!Number.isInteger(position)) return acc;

    // A row carrying neither key is still sent, so the server can answer with a
    // reason. Dropping it here is what used to lose every row of a PMS file in
    // silence.
    const dpDoctorId = dpDoctorIdRaw ? Number(dpDoctorIdRaw) : undefined;
    if (dpDoctorId !== undefined && (!Number.isFinite(dpDoctorId) || dpDoctorId <= 0)) return acc;

    acc.push({
      categoryExternalId,
      ...(dpDoctorId !== undefined && { dpDoctorId }),
      ...(hsid && { hsid }),
      position,
      ...(participantName && { participantName }),
    });
    return acc;
  }, []);
}

export function getWinnerPreviews(rows: ImportWinnerRow[]): WinnerPreview[] {
  const map = new Map<number, number>();
  for (const row of rows) {
    map.set(row.categoryExternalId, (map.get(row.categoryExternalId) ?? 0) + 1);
  }
  return [...map.entries()].map(([categoryExternalId, winnerCount]) => ({
    categoryExternalId,
    winnerCount,
  }));
}

const CSV_HEADERS = [
  'category_external_id',
  'dp_doctor_id',
  'hsid',
  'position',
  'participant_name',
  'error_reason',
] as const;

export function buildFailedWinnerRowsCsv(failedRows: FailedWinnerRow[]): string {
  const header = CSV_HEADERS.join(',');
  const lines = failedRows.map(({ row, reason }) =>
    [
      String(row.categoryExternalId),
      row.dpDoctorId === undefined ? '' : String(row.dpDoctorId),
      escapeCsvField(row.hsid ?? ''),
      String(row.position),
      escapeCsvField(row.participantName ?? ''),
      escapeCsvField(reason),
    ].join(','),
  );
  return [header, ...lines].join('\n');
}

export function validateWinnerHeaders(content: string): string | null {
  const lines = content.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return 'The file must have a header row and at least one data row.';

  const { headers } = parseCsvHeaders(lines[0]!);

  for (const expected of REQUIRED_HEADERS) {
    if (!headers.includes(expected)) {
      return `Missing required column: "${expected}"`;
    }
  }

  if (!KEY_HEADERS.some((header) => headers.includes(header))) {
    return 'Missing the winner column: "dp_doctor_id" for a marketplace edition, or "hsid" for a PMS one.';
  }

  return null;
}
